import { Person } from "@turizi/shared";
import { IconStarFilled, IconMail, IconPhone, IconId, IconPlus, IconSelectAll, IconArrowLeft, IconSelector } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TagIcon } from "@/lib/tag-utils";
import CardModal from "./card-modal";
import { Link } from "react-router-dom";
import { PERSONS_PATH } from "@/lib/navigation";
import CreateOrSelectPerson from "./create-or-select-person-component";

interface Props {
    onSelect: (item: Person) => void;
    onSelectMultiple?: (items: Person[]) => void;
    className?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    variant?: 'default' | 'unstyled';
    selected?: Person | null;
    placeholder?: JSX.Element;
    owned_by_me?: boolean | undefined;
    loading?: boolean;
    disabled?: boolean;
    exclude?: number[]; //IDs to exclude from the list
    showIcon?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    autoFocus?: boolean;
    opened?: boolean;
    title?: React.ReactNode;
    modalSize?: string;
}

export default function PaxSelector({
    variant = 'default',
    onSelect,
    className,
    size = 'sm',
    selected,
    placeholder,
    disabled = false,
    showIcon = true,
    onOpen,
    onClose,
    opened,
    modalSize,
}: Props) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [localOpened, setLocalOpened] = useState(false);

    const isModalOpened = opened !== undefined ? opened : localOpened;

    const handleClose = () => {
        if (opened === undefined) {
            setLocalOpened(false);
        }
        onClose?.();
    };

    const handleSelect = (item: Person) => {
        onSelect(item);
        setSearchQuery('');
        handleClose();
    };

    const selectedLink = selected ? `${PERSONS_PATH}/${selected?.id}` : '';

    return (
        <>
            {opened === undefined && (
                <div
                    className="w-full"
                    onClick={() => {
                        if (!disabled) {
                            setSearchQuery('');
                            setLocalOpened(true);
                            onOpen?.();
                        }
                    }}
                >
                    <div
                        className={`
                            ${className || 'w-full'}
                            ${variant === 'default'
                                ? disabled ? 'solid-box-border-tinted' : 'solid-box-border-hover'
                                : null
                            }
                            rounded-md transition-all
                            text-${size}
                            hover:text-black
                            h-10 py-2 px-1
                            flex items-center justify-between
                        `}
                    >
                        {!selected && (
                            <div className="flex-1">
                                {
                                    placeholder ||
                                    (
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm text-gray-400">{t('select_pax') + '...'}</div>
                                        </div>
                                    )
                                }
                            </div>
                        )}
                        {selected && (
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                                {showIcon && (
                                    <div className="text-xxs flex flex-col items-center text-gray-600 bg-blue-50 p-1 rounded-md">
                                        {selected.category && <TagIcon name={selected.category} hideText noBackground size="xs" />}
                                        #{selected.id}
                                    </div>
                                )}
                                <Link to={selectedLink}>
                                    {selected.name}
                                </Link>
                            </div>
                        )}
                        {!disabled && <IconSelector size={16} stroke={1} className="text-gray-700 flex-shrink-0" />}
                    </div>
                </div>
            )}

            <CardModal
                size={modalSize || "820px"}
                withCloseButton={true}
                opened={isModalOpened}
                onClose={handleClose}
                header={(
                    <div>
                        {t('create_select_new_pax')}
                    </div>
                )}
            >
                <CreateOrSelectPerson
                    preloadName={searchQuery}
                    onCreate={handleSelect}
                />
            </CardModal>
        </>
    );
}